import type { AgentAttributionSource } from "@better-ccflare/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type { Account, RequestPayload, RequestResponse } from "../api";
import { queryKeys } from "../lib/query-keys";

// Connection pool management
const CONNECTION_POOL = new Map<
	string,
	{
		connection: EventSource;
		refCount: number;
		lastUsed: number;
		heartbeat: NodeJS.Timeout;
	}
>();

// Cleanup inactive connections periodically
const CLEANUP_INTERVAL = 30000; // 30 seconds
const CONNECTION_TIMEOUT = 60000; // 1 minute
const HEARTBEAT_INTERVAL = 15000; // 15 seconds
const RECONNECT_MAX_DELAY = 30000;
const HIDDEN_RECONNECT_THRESHOLD = 30000;

// EventSource.readyState values, as numbers so the helpers below stay pure
const ES_OPEN = 1;
const ES_CLOSED = 2;

// Global cleanup for connection pool
let globalCleanupInterval: Timer | null = null;

const startCleanupInterval = () => {
	if (!globalCleanupInterval) {
		globalCleanupInterval = setInterval(() => {
			const now = Date.now();
			for (const [key, conn] of CONNECTION_POOL.entries()) {
				if (now - conn.lastUsed > CONNECTION_TIMEOUT && conn.refCount === 0) {
					console.log(`Cleaning up inactive SSE connection: ${key}`);
					conn.connection.close();
					clearInterval(conn.heartbeat);
					CONNECTION_POOL.delete(key);
				}
			}
		}, CLEANUP_INTERVAL);
	}
};

const stopCleanupInterval = () => {
	if (globalCleanupInterval) {
		clearInterval(globalCleanupInterval);
		globalCleanupInterval = null;
	}
};

// Start cleanup interval when first connection is created
const getOrCreateCleanupInterval = () => {
	if (!globalCleanupInterval) {
		startCleanupInterval();
	}
	return globalCleanupInterval;
};

let requestsSseSeq = 0;

export function getRequestsSseSeq(): number {
	return requestsSseSeq;
}

export function shouldRefetchOnOpen(
	hasOpenedBefore: boolean,
	retryCount: number,
): boolean {
	return hasOpenedBefore || retryCount > 0;
}

export function reconnectDelay(
	retryCount: number,
	random: () => number = Math.random,
): number {
	const base = Math.min(1000 * 2 ** retryCount, RECONNECT_MAX_DELAY);
	return Math.round(base + random() * base * 0.25);
}

export function shouldReconnectOnWake(
	readyState: number | undefined,
	hiddenMs = 0,
	isOnlineEvent = false,
): boolean {
	return (
		readyState !== ES_OPEN ||
		isOnlineEvent ||
		hiddenMs > HIDDEN_RECONNECT_THRESHOLD
	);
}

export function shouldReconnectOnHeartbeat(readyState: number): boolean {
	return readyState === ES_CLOSED;
}

function pruneDetailsMap(
	map: Map<string, RequestResponse>,
	requests: RequestPayload[],
): Map<string, RequestResponse> {
	if (map.size === 0) return map;
	const ids = new Set(requests.map((r) => r.id));
	let stale = false;
	for (const id of map.keys()) {
		if (!ids.has(id)) {
			stale = true;
			break;
		}
	}
	if (!stale) return map;
	const pruned = new Map<string, RequestResponse>();
	for (const [id, value] of map) {
		if (ids.has(id)) pruned.set(id, value);
	}
	return pruned;
}

export function useRequestStream(limit = 200) {
	const queryClient = useQueryClient();
	const connectionKey = `requests-stream-${limit}`;
	const isMountedRef = useRef(true);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const reconnectNowRef = useRef<() => void>(() => {});

	// Heartbeat to detect a source that was closed without reaching our error
	// handler. CONNECTING is left alone: the browser or our error path is
	// already handling it.
	const setupHeartbeat = useCallback((es: EventSource, key: string) => {
		return setInterval(() => {
			if (shouldReconnectOnHeartbeat(es.readyState)) {
				console.log(`Connection ${key} is closed, reconnecting`);
				const pooled = CONNECTION_POOL.get(key);
				if (pooled) {
					clearInterval(pooled.heartbeat);
					CONNECTION_POOL.delete(key);
				}
				reconnectNowRef.current();
			}
		}, HEARTBEAT_INTERVAL);
	}, []);

	// Safe message handler with try-catch that directly updates React Query cache
	const handleMessage = useCallback(
		(ev: MessageEvent) => {
			if (!isMountedRef.current) return;

			try {
				const evt = JSON.parse(ev.data) as
					| {
							type: "start";
							id: string;
							method: string;
							path: string;
							timestamp: number;
							accountId: string | null;
							statusCode: number;
							agentUsed: string | null;
							agentAttributionSource?: AgentAttributionSource | null;
					  }
					| { type: "summary"; payload: RequestResponse };

				requestsSseSeq++;
				queryClient.setQueryData(
					queryKeys.requests(limit),
					(
						current:
							| {
									requests: RequestPayload[];
									detailsMap: Map<string, RequestResponse> | RequestResponse[];
							  }
							| undefined,
					) => {
						if (!current) return current;

						// Ensure detailsMap is a Map
						const currentDetailsMap =
							current.detailsMap instanceof Map
								? current.detailsMap
								: new Map(
										(current.detailsMap as RequestResponse[]).map((s) => [
											s.id,
											s,
										]),
									);

						if (evt.type === "start") {
							// Look up account name from cache
							const accounts = queryClient.getQueryData<Account[]>(
								queryKeys.accounts(),
							);
							const account = accounts?.find((a) => a.id === evt.accountId);

							// Create a lightweight placeholder payload.
							// `bodiesOmitted: true` is required so that opening the
							// details modal or pressing Copy-as-JSON triggers the
							// lazy /api/requests/payload/:id fetch — without it the
							// modal shows empty headers/body and the copy returns
							// nulled-out bodies.
							//
							// `response: null` when no status is known yet so the
							// status chip (guarded by `statusCode != null`) does NOT
							// render a "0" before the response lands. The summary
							// handler below patches response.status from
							// `evt.payload.statusCode` once the request completes.
							const hasStatus = evt.statusCode > 0;
							const placeholder: RequestPayload = {
								id: evt.id,
								request: { headers: {}, body: null },
								response: hasStatus
									? {
											status: evt.statusCode,
											headers: {},
											body: null,
										}
									: null,
								meta: {
									timestamp: evt.timestamp,
									path: evt.path,
									method: evt.method,
									accountId: evt.accountId || undefined,
									accountName: account?.name,
									success: false,
									pending: true,
									agentUsed: evt.agentUsed || undefined,
									agentAttributionSource:
										evt.agentAttributionSource || undefined,
									rateLimited: evt.statusCode === 429,
									bodiesOmitted: true,
								},
							};

							// Check if this request already exists
							const existingIndex = current.requests.findIndex(
								(r) => r.id === evt.id,
							);
							if (existingIndex >= 0) {
								// Update existing placeholder
								const newRequests = [...current.requests];
								newRequests[existingIndex] = placeholder;
								return {
									...current,
									requests: newRequests,
									detailsMap: currentDetailsMap,
								};
							}

							// Add new placeholder at the beginning
							const nextRequests = [placeholder, ...current.requests].slice(
								0,
								limit,
							);
							return {
								...current,
								requests: nextRequests,
								detailsMap: pruneDetailsMap(currentDetailsMap, nextRequests),
							};
						}
						// Update details map with summary
						const map = new Map(currentDetailsMap);
						map.set(evt.payload.id, evt.payload);

						// Update the request if it exists
						const requestIndex = current.requests.findIndex(
							(r) => r.id === evt.payload.id,
						);
						if (requestIndex >= 0) {
							const newRequests = [...current.requests];
							// Update meta to remove pending status. Preserve
							// `bodiesOmitted: true` so consumers continue to lazy-
							// load bodies, and refresh `rateLimited` from the final
							// statusCode (the placeholder set it from `evt.statusCode`
							// which is 0 until the response lands).
							if (newRequests[requestIndex].meta) {
								newRequests[requestIndex] = {
									...newRequests[requestIndex],
									// Refresh response.status from the final summary —
									// the start placeholder set it to 0 before the HTTP
									// response landed, and the new header-row chip uses
									// a `statusCode != null` guard (0 is not null), so
									// without this every SSE-completed request would
									// permanently display a grey "0" chip.
									response:
										evt.payload.statusCode != null
											? {
													...(newRequests[requestIndex].response ?? {
														headers: {},
														body: null,
													}),
													status: evt.payload.statusCode,
												}
											: null,
									meta: {
										...newRequests[requestIndex].meta,
										pending: false,
										success: evt.payload.success,
										rateLimited: evt.payload.rateLimited,
										bodiesOmitted: true,
									},
								};
							}
							return {
								...current,
								requests: newRequests,
								detailsMap: pruneDetailsMap(map, newRequests),
							};
						}

						return {
							...current,
							detailsMap: pruneDetailsMap(map, current.requests),
						};
					},
				);
			} catch (error) {
				console.error("Error parsing SSE message:", error);
			}
		},
		[queryClient, limit],
	);

	// Connect with connection pooling
	const connect = useCallback(
		(retryCount = 0, forceRefetch = false): EventSource => {
			// Ensure cleanup interval is running
			getOrCreateCleanupInterval();

			// Check if we have a healthy connection in the pool
			const pooled = CONNECTION_POOL.get(connectionKey);
			if (pooled && pooled.connection.readyState === EventSource.OPEN) {
				pooled.refCount++;
				pooled.lastUsed = Date.now();
				console.log(
					`Reusing pooled SSE connection: ${connectionKey} (refCount: ${pooled.refCount})`,
				);
				return pooled.connection;
			}

			// Close any existing connection for this key
			if (pooled) {
				pooled.connection.close();
				clearInterval(pooled.heartbeat);
				CONNECTION_POOL.delete(connectionKey);
			}

			console.log(`Creating new SSE connection: ${connectionKey}`);
			const es = new EventSource("/api/requests/stream");

			// Setup event handlers
			let hasOpened = false;
			let attempt = retryCount;
			es.addEventListener("open", () => {
				if (!isMountedRef.current) {
					es.close();
					return;
				}
				if (forceRefetch || shouldRefetchOnOpen(hasOpened, retryCount)) {
					queryClient.invalidateQueries({
						queryKey: queryKeys.requests(limit),
					});
				}
				hasOpened = true;
				attempt = 0;
				console.log(`SSE connection established: ${connectionKey}`);
			});

			es.addEventListener("message", handleMessage);

			es.addEventListener("error", (error) => {
				console.error(`SSE connection error (${connectionKey}):`, error);

				// Stop the browser's built-in auto-reconnect; we manage retries ourselves
				es.close();

				// Remove from pool
				const pooled = CONNECTION_POOL.get(connectionKey);
				if (pooled) {
					clearInterval(pooled.heartbeat);
					CONNECTION_POOL.delete(connectionKey);
				}

				if (isMountedRef.current) {
					const delay = reconnectDelay(attempt);
					console.log(
						`Reconnecting ${connectionKey} in ${delay}ms (attempt ${attempt + 1})`,
					);

					if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
					retryTimerRef.current = setTimeout(() => {
						retryTimerRef.current = null;
						if (isMountedRef.current) {
							connect(attempt + 1);
						}
					}, delay);
				}
			});

			// Add to pool with heartbeat
			const heartbeat = setupHeartbeat(es, connectionKey);
			CONNECTION_POOL.set(connectionKey, {
				connection: es,
				refCount: 1,
				lastUsed: Date.now(),
				heartbeat,
			});

			return es;
		},
		[connectionKey, handleMessage, setupHeartbeat, queryClient, limit],
	);

	const reconnectNow = useCallback(() => {
		if (!isMountedRef.current) return;
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		const pooled = CONNECTION_POOL.get(connectionKey);
		if (pooled) {
			pooled.connection.close();
			clearInterval(pooled.heartbeat);
			CONNECTION_POOL.delete(connectionKey);
		}
		connect(0, true);
	}, [connect, connectionKey]);
	reconnectNowRef.current = reconnectNow;

	useEffect(() => {
		isMountedRef.current = true;
		const es = connect();

		// A sleeping laptop or backgrounded tab can kill the stream silently, so
		// refetch the snapshot and reconnect (if needed) when we come back.
		let hiddenAt: number | null = null;
		const recover = (hiddenMs: number, isOnlineEvent: boolean) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.requests(limit) });
			if (
				shouldReconnectOnWake(
					CONNECTION_POOL.get(connectionKey)?.connection.readyState,
					hiddenMs,
					isOnlineEvent,
				)
			) {
				reconnectNow();
			}
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				hiddenAt = Date.now();
				return;
			}
			const hiddenMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
			hiddenAt = null;
			recover(hiddenMs, false);
		};
		const onOnline = () => recover(0, true);
		document.addEventListener("visibilitychange", onVisibilityChange);
		window.addEventListener("online", onOnline);

		// Cleanup function
		return () => {
			isMountedRef.current = false;
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.removeEventListener("online", onOnline);
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
				retryTimerRef.current = null;
			}

			const pooled = CONNECTION_POOL.get(connectionKey);
			if (pooled) {
				pooled.refCount--;
				pooled.lastUsed = Date.now();

				// Close connection immediately if no more references
				if (pooled.refCount <= 0) {
					console.log(`No more references for ${connectionKey}, closing`);
					pooled.connection.close();
					clearInterval(pooled.heartbeat);
					CONNECTION_POOL.delete(connectionKey);
				}
			} else if (es) {
				es.close();
			}
		};
	}, [connect, connectionKey, reconnectNow, queryClient, limit]);

	// Global cleanup on unmount
	useEffect(() => {
		return () => {
			isMountedRef.current = false;

			// Stop global cleanup interval if no more connections
			if (CONNECTION_POOL.size === 0) {
				stopCleanupInterval();
			}
		};
	}, []);
}

// Export global cleanup function for app shutdown
export const cleanupRequestStream = () => {
	// Stop the global cleanup interval
	stopCleanupInterval();

	// Close all pooled connections
	for (const [key, conn] of CONNECTION_POOL.entries()) {
		console.log(`Cleaning up SSE connection on shutdown: ${key}`);
		conn.connection.close();
		clearInterval(conn.heartbeat);
	}
	CONNECTION_POOL.clear();
};
